import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Flame, ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [resetLink, setResetLink] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      if (data.reset_link) setResetLink(data.reset_link);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: `url('https://popmenucloud.com/cdn-cgi/image/width=1200,height=1200,fit=scale-down,format=auto,quality=60/jkweqdot/f7f92250-a632-48be-b5df-753100fdb36a.jpg')`
      }} />
      <div className="absolute inset-0 bg-black/50" />
      <Card className="relative z-10 w-full max-w-md mx-4 border-stone-200 shadow-2xl bg-white/95 backdrop-blur-sm rounded-2xl" data-testid="forgot-password-card">
        <CardHeader className="text-center pb-2 pt-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-12 h-12 bg-[#1B2A4A] rounded-xl flex items-center justify-center">
              <Flame className="w-7 h-7 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Reset Password
          </CardTitle>
        </CardHeader>
        <CardContent className="px-6 pb-8 pt-2">
          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-password-form">
              <p className="text-sm text-stone-500 text-center">Enter your email to receive a password reset link</p>
              {error && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="forgot-error">{error}</div>}
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@grillsmith.com" className="rounded-xl border-stone-200 h-11"
                  data-testid="forgot-email-input" required />
              </div>
              <Button type="submit" disabled={loading}
                className="w-full h-11 bg-[#1B2A4A] text-white rounded-xl font-medium hover:bg-[#243658]"
                data-testid="forgot-submit-btn">
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          ) : (
            <div className="text-center space-y-4" data-testid="forgot-success">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
              <p className="text-stone-700 font-medium">Reset link sent!</p>
              <p className="text-sm text-stone-500">Check your email for the password reset link.</p>
              {resetLink && (
                <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 break-all">
                  <p className="font-medium mb-1">Dev mode - Reset link:</p>
                  <a href={resetLink} className="underline text-[#1B2A4A]" data-testid="reset-link">{resetLink}</a>
                </div>
              )}
            </div>
          )}
          <div className="mt-4 text-center">
            <Link to="/login" className="text-sm text-[#1B2A4A] hover:underline inline-flex items-center gap-1" data-testid="back-to-login">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Sign In
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
